import { mod } from "@/utils/math"
import { MouseEvent, PointerEvent, useEffect, useRef, useState } from "react"

interface Props {
	hue: number
	saturation: number
	onChange?: (hue: number, saturation: number) => void
	saveChange: (name: string) => void
	className?: string
}

export const ColorWheel = (props: Props) => {
	/* Constants */

	const pointerSize = 16

	/* State */

	const containerElement = useRef<HTMLDivElement>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [pointerTransform, setPointerTransform] = useState({ x: 0, y: 0 })

	useEffect(() => {
		setPointerTransform(getCenterPoint())
	}, [containerElement])

	useEffect(() => {
		if (isDragging === true) {
			return
		}

		const center = getCenterPoint()
		const radius = getRadius()
		const angle = props.hue * Math.PI * 2
		const distance = props.saturation * (radius - pointerSize / 2)

		const newPoint = {
			x: center.x + distance * Math.cos(angle),
			y: center.y - distance * Math.sin(angle),
		}

		setPointerTransform(newPoint)
	}, [props.hue, props.saturation])

	const getRadius = () => {
		const width = containerElement.current?.offsetWidth ?? 48
		return width / 2
	}

	const getCenterPoint = () => {
		const radius = getRadius()

		return {
			x: radius - pointerSize / 2,
			y: radius - pointerSize / 2,
		}
	}

	/* Actions */

	const handleDragging = (event: PointerEvent) => {
		const target = event.target as HTMLCanvasElement

		/* Calculating precise position of the point inside the circle */

		const center = getCenterPoint()
		const radius = getRadius()

		let currentPositionX = pointerTransform.x
		let currentPositionY = pointerTransform.y

		if (event.type === "pointerdown") {
			currentPositionX = event.nativeEvent.offsetX - pointerSize / 2
			currentPositionY = event.nativeEvent.offsetY - pointerSize / 2
		}

		const dx = event.nativeEvent.movementX
		const dy = event.nativeEvent.movementY

		const vx = currentPositionX + dx - center.x
		const vy = currentPositionY + dy - center.y
		const vm = Math.sqrt(vx * vx + vy * vy)

		const newPoint = {
			x: currentPositionX + dx,
			y: currentPositionY + dy,
		}

		if (vm >= radius - pointerSize / 2) {
			newPoint.x = center.x + (vx / vm) * (radius - pointerSize / 2)
			newPoint.y = center.y + (vy / vm) * (radius - pointerSize / 2)
		}

		const hue =
			mod(
				Math.atan2(newPoint.y - center.y, newPoint.x - center.x) * (360 / (-Math.PI * 2)),
				360,
			) / 360
		const saturation = Math.min(vm, radius - pointerSize / 2) / (radius - pointerSize / 2)

		/* Dealing with events */

		if (event.type === "pointerdown") {
			setIsDragging(true)
			target.setPointerCapture(event.pointerId)

			setPointerTransform(newPoint)
			props.onChange && props.onChange(hue, saturation)
		} else if (event.type === "pointermove") {
			if (isDragging === false) {
				return
			}

			setPointerTransform(newPoint)
			props.onChange && props.onChange(hue, saturation)
		} else {
			setIsDragging(false)
			target.releasePointerCapture(event.pointerId)

			props.saveChange(`Hue ${hue.toFixed(2)} Saturation ${saturation.toFixed(2)}`)
		}
	}

	const handleReset = (event: MouseEvent<HTMLDivElement>) => {
		if (event.detail !== 2) {
			return
		}

		event.preventDefault()

		setPointerTransform(getCenterPoint())
		props.onChange && props.onChange(0, 0)
		props.saveChange(`Reset color grading`)
	}

	/* Render */

	return (
		<div
			ref={containerElement}
			onMouseUp={handleReset}
			onPointerDown={handleDragging}
			onPointerMove={handleDragging}
			onPointerUp={handleDragging}
			className={`relative ${isDragging === true ? "cursor-none" : ""} ${props.className}`}
		>
			{/* HSL Wheel background and white overlay */}
			<div
				className="pointer-events-none relative size-full overflow-hidden rounded-full bg-black"
				style={{
					background: "conic-gradient(red, magenta, blue, aqua, lime, yellow, red)",
					transform: "rotateZ(90deg)",
				}}
			/>
			<div
				className="pointer-events-none"
				style={{
					position: "absolute",
					borderRadius: "50%",
					background: "radial-gradient(circle closest-side, #fff, transparent)",
					inset: 0,
				}}
			/>
			{/* Center dot */}
			<div
				className="pointer-events-none absolute z-10 size-[2px] rounded-full bg-black opacity-50"
				style={{
					top: "50%",
					left: "50%",
					transform: "translateX(-50%) translateY(-50%)",
				}}
			/>
			{/* Pointer */}
			<div
				className="pointer-events-none absolute z-10 rounded-full border-2 border-black opacity-75"
				style={{
					width: `${pointerSize}px`,
					height: `${pointerSize}px`,
					top: "0",
					left: "0",
					transform: `translate(${pointerTransform.x}px, ${pointerTransform.y}px)`,
				}}
			/>
		</div>
	)
}
